import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IDeliveryAddress, initialDeliveryAddress } from '../dto/IDeliveryAddress';
import { IAllStates } from '../../../../store/rootReducer';
import { UserService } from '../service';
import { ErrorToast, SuccessToast } from '../../../../utils/toasters';
import PhoneInput from 'react-phone-input-2';
import { CustomAutocomplete } from '../../../../shared/components/Autocomplete';
import { UserRoles } from '../../../../enum/UserRoles';
import CardLayout from '../../../../shared/components/card/CardLayout';
import ToggleSwitch from '../../../../shared/components/ToggleSwitch';

interface IDeliveryAddressModalProps {
    show: boolean;
    closeModal: () => void;
    userId?: number;
    updateList?: React.Dispatch<React.SetStateAction<boolean>>;
}

type DeliveryContext = 'influencer' | 'agencyOwn' | 'agencyInfluencer';

export const DeliveryAddressModal = ({ show, closeModal, userId, updateList }: IDeliveryAddressModalProps) => {
    const { t } = useTranslation();
    const country = useSelector((states: IAllStates) => states.enums.country);
    const userType = useSelector((states: IAllStates) => states.auth.user.userType);

    const [formData, setFormData] = useState<IDeliveryAddress>(initialDeliveryAddress);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const isAgencyRole = userType === UserRoles.AGENCY || userType === UserRoles.AGENCY_MASTER;

    const { context, targetUserId } = useMemo<{ context: DeliveryContext; targetUserId?: number }>(() => {
        if (isAgencyRole) {
            return userId ? { context: 'agencyInfluencer', targetUserId: userId } : { context: 'agencyOwn' };
        }

        return { context: 'influencer' };
    }, [isAgencyRole, userId]);

    const showToggle = isAgencyRole && context === 'agencyInfluencer' && !!targetUserId;

    useEffect(() => {
        if (show) {
            fetchDeliveryAddress();
        } else {
            setFormData(initialDeliveryAddress);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, context, targetUserId]);

    const fetchDeliveryAddress = async () => {
        setIsLoading(true);
        try {
            let data: IDeliveryAddress;

            if (context === 'agencyOwn') {
                data = await UserService.getAgencyDeliveryAddress();
            } else if (context === 'agencyInfluencer' && targetUserId) {
                data = await UserService.getAgencyInfluencerDeliveryAddress(targetUserId);
            } else {
                data = await UserService.getInfluencerDeliveryAddress();
            }

            if (data) {
                setFormData(prev => ({ ...prev, ...data }));
            }
        } catch (err) {
            ErrorToast(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeParams = (paramKey: string, paramValue: any) => {
        setFormData((prev: IDeliveryAddress) => ({ ...prev, [paramKey]: paramValue }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleChangeParams(e.target.name, e.target.value);
    };

    const handleCountryChange = (_: unknown, value: any) => {
        handleChangeParams('country', value?.code ?? value ?? '');
    };

    const handlePhoneChange = (value: string) => {
        handleChangeParams('phoneNumber', value);
    };

    const handleToggleOwnAddress = () => {
        handleChangeParams('useOwnAddress', !formData.useOwnAddress);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (context === 'agencyOwn') {
                await UserService.addAgencyDeliveryAddress(formData);
            } else if (context === 'agencyInfluencer' && targetUserId) {
                await UserService.addAgencyInfluencerDeliveryAddress(formData, targetUserId);
                if (updateList) {
                    updateList(prevState => !prevState);
                }
            } else {
                await UserService.addInfluencerDeliveryAddress(formData);
            }

            SuccessToast(t('influencer.delivery.modal.success'));
            closeModal();
        } catch (err) {
            ErrorToast(err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={closeModal} centered size="lg" className="delivery-address-modal default-radius">
            <form onSubmit={handleSubmit}>
                <Modal.Body className="p-4">
                    <h4 className="font-weight-bold mb-4">{t('influencer.delivery.modal.title')}</h4>
                    {showToggle && (
                        <CardLayout classCard="mb-3" classBody="d-flex align-items-center justify-content-between ">
                            <div className="pr-3">
                                <p className="font-weight-semi-bold text-dark mb-1">
                                    {t('influencer.delivery.modal.ship_to_influencer')}
                                </p>
                                <p className="text-muted mb-0 font-size-sm">
                                    {t('influencer.delivery.modal.ship_to_influencer_desc')}
                                </p>
                            </div>
                            <ToggleSwitch
                                small={true}
                                id="use-own-address"
                                onChange={handleToggleOwnAddress}
                                checked={!!formData.useOwnAddress}
                            />
                        </CardLayout>
                    )}
                    <Row className="gy-3">
                        <Col md={6}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                name="firstName"
                                label={t('influencer.delivery.modal.first_name')}
                                value={formData.firstName}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={6}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                name="lastName"
                                label={t('influencer.delivery.modal.last_name')}
                                value={formData.lastName}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={12}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="company"
                                label={t('influencer.delivery.modal.company')}
                                placeholder={t('influencer.delivery.modal.optional')}
                                value={formData.company}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={9}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                name="street"
                                label={t('influencer.delivery.modal.street')}
                                value={formData.street}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={3}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                name="streetNumber"
                                label={t('influencer.delivery.modal.street_number')}
                                value={formData.streetNumber}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={12}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="addressLine2"
                                label={t('influencer.delivery.modal.address_line_2')}
                                placeholder={t('influencer.delivery.modal.address_line_2_placeholder')}
                                value={formData.addressLine2}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={4}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                name="zip"
                                label={t('influencer.delivery.modal.zip')}
                                value={formData.zip}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={4}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                name="city"
                                label={t('influencer.delivery.modal.city')}
                                value={formData.city}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="state"
                                label={t('influencer.delivery.modal.state')}
                                value={formData.state}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={6}>
                            <CustomAutocomplete
                                dataArray={country}
                                isWithImage={true}
                                inputLabel="country"
                                isMultiple={false}
                                isRequired={true}
                                defaultArray={formData.country ?? []}
                                photoLabel="countryFlag"
                                queryFilterKey="country"
                                inputLabelTranslation={t('influencer.delivery.modal.country')}
                                handleChangeParams={handleCountryChange}
                            />
                        </Col>
                        <Col md={6}>
                            <PhoneInput
                                inputStyle={{ width: '100%' }}
                                country={'de'}
                                value={formData.phoneNumber}
                                onChange={handlePhoneChange}
                                specialLabel={t('influencer.delivery.modal.phone_number')}
                                inputProps={{ name: 'phoneNumber', required: true }}
                            />
                        </Col>

                        <Col md={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                variant="outlined"
                                size="small"
                                name="deliveryInstructions"
                                label={t('influencer.delivery.modal.delivery_instructions')}
                                placeholder={t('influencer.delivery.modal.delivery_instructions_placeholder')}
                                value={formData.deliveryInstructions}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn btn-outline-primary mr-2" onClick={closeModal}>
                            {t('influencer.delivery.modal.close')}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSaving || isLoading}>
                            {t('influencer.delivery.modal.save')}
                        </button>
                    </div>
                </Modal.Footer>
            </form>
        </Modal>
    );
};
